import { Checkbox, Label } from "@setu/ui";
import type { KeychainOffer } from "./useKeychain";

/**
 * "Remember this on my keychain", offered next to the passphrase field.
 *
 * Renders nothing in a browser — there is no keychain to offer, and a disabled
 * checkbox explaining an absent feature is worse than silence.
 *
 * The wording is deliberately specific about the trade rather than reassuring about
 * it. What the user gains is not having to type a passphrase on every launch. What
 * they give up is the property that a locked identity cannot sign until a human
 * types something: once the passphrase is on the keychain, anything running as this
 * user in this app can unlock the key. That is the right choice on a personal
 * machine and the wrong one on a shared login, and only the person in front of the
 * screen knows which they have — so the box starts unticked.
 */
export function KeychainOptIn({ offer }: { offer: KeychainOffer }) {
  if (!offer.available) return null;

  return (
    <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
      <Checkbox
        id="keychain-remember"
        className="mt-0.5"
        checked={offer.enabled}
        onCheckedChange={(checked) => offer.setEnabled(checked === true)}
      />
      <div className="min-w-0 space-y-0.5">
        <Label htmlFor="keychain-remember" className="text-xs">
          Remember this passphrase in my keychain
        </Label>
        <p className="text-2xs text-muted-foreground">
          Setu will unlock itself on this computer instead of asking. Your key
          stays encrypted on disk; the passphrase moves to the operating
          system's keychain, out of reach of anything in the app's own storage.
          Leave it off on a shared login.
        </p>
      </div>
    </div>
  );
}
